import React, {useCallback, useEffect} from 'react';
import {assert} from 'assert-ts';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import {useLocalization} from 'localization';
import {AgentTypes, selectSettingsAgentsSearchText} from 'services/settings';
import {isAgentSubType} from 'services/utils';
import {useAppSelector} from 'store/hooks';
import {FlexBox, Scene, Text} from 'components';
import {useOpenSearch, useSceneExtensionsContext} from 'sceneExtensions';
import {AgentsToolbar} from './components';
import NorafTabs from './components/NorafTabs';

export const SearchAgentsScene: React.FC = () => {
  const {t} = useLocalization();
  const navigate = useNavigate();

  const agentSearchText = useAppSelector(selectSettingsAgentsSearchText);

  const {currentExtension} = useSceneExtensionsContext();
  const openSearch = useOpenSearch();

  const canEditEntity = useCallback(
    (entityType: Concept) => isAgentSubType(entityType),
    [],
  );

  const customHandleEditAgent = useCallback(
    (entityType: Concept, entityId: string) => {
      assert(
        isAgentSubType(entityType),
        'handleEditAgent: agent type expected',
        {entityType},
      );

      navigate(`/agent/${entityId}`);
    },
    [navigate],
  );

  const customHandleAddAgent = useCallback(
    (entityType: Concept) => {
      assert(
        isAgentSubType(entityType),
        'handleCreateAgent: agent type expected',
        {entityType},
      );

      navigate(`/agent/new?type=${entityType}&suggestion=${agentSearchText}`);
    },
    [navigate, agentSearchText],
  );

  const handleOpenSearch = useCallback(() => {
    openSearch({
      entity: Concept.agent,
      canEditEntity,
      handleEditEntity: customHandleEditAgent,
      handleAddEntity: customHandleAddAgent,
    });
  }, [canEditEntity, customHandleAddAgent, customHandleEditAgent, openSearch]);

  useEffect(() => {
    handleOpenSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Scene
      browserTitle={t('appbar.agents')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header>
        <FlexBox horizontal justifyContent={'space-between'}>
          <Text variant="h1">{t('appbar.agents')}</Text>

          <AgentsToolbar
            agentTypes={AgentTypes}
            onSearch={handleOpenSearch}
            onAdd={customHandleAddAgent}
            onAddWizard={customHandleAddAgent}
          />
        </FlexBox>
      </Scene.Header>
      <Scene.Content>
        <NorafTabs />
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
